import React, { useCallback } from 'react';

import { useActualIncidentId } from 'components/IAP/hooks/commonHooks';
import {
  IncidentMedicalTransportation,
  useIncidentMedicalTransportations,
  useCreateIncidentMedicalTransportation,
  useEditIncidentMedicalTransportation,
} from 'components/IAP/hooks/incidentMedicalTransportationHooks';
import { MedicalTransportationBase } from 'types/iapBaseTypes';

import {
  MedicalPlanTable,
  type MedicalPlanColumn,
  type MedicalPlanDraft,
} from './MedicalPlanTable';
import { useRecordMedicalPlanVersion } from './medicalPlanVersionRecording';

const COLUMNS: MedicalPlanColumn[] = [
  {
    kind: 'text',
    field: 'ambulance_service',
    header: 'Ambulance Service',
    placeholder: 'Ambulance Service *',
    maxLength: 200,
  },
  {
    kind: 'text',
    field: 'address',
    header: 'Location',
    placeholder: 'Location',
  },
  {
    kind: 'text',
    field: 'contact_number',
    header: 'Contact Information',
    placeholder: 'Contact',
    maxLength: 40,
  },
  {
    kind: 'checkbox',
    field: 'paramedics_on_board',
    header: 'Paramedics',
  },
  {
    kind: 'radioPair',
    header: 'Level of Service',
    options: [
      { field: 'als', label: 'ALS' },
      { field: 'bls', label: 'BLS' },
    ],
  },
];

const EMPTY_DRAFT: MedicalPlanDraft = {
  ambulance_service: '',
  address: '',
  contact_number: '',
  paramedics_on_board: false,
  als: false,
  bls: false,
};

const toEditPayload = (
  entity: IncidentMedicalTransportation,
  override: MedicalPlanDraft
): MedicalTransportationBase & {
  id: string;
  transportation_id: string;
  archived: boolean;
} => ({
  id: entity.id,
  transportation_id: entity.transportation_id,
  ambulance_service: entity.ambulance_service,
  description: entity.description,
  address: entity.address,
  contact_number: entity.contact_number,
  paramedics_on_board: entity.paramedics_on_board,
  als: entity.als,
  bls: entity.bls,
  archived: entity.archived,
  ...override,
});

type Props = { readOnly?: boolean };

export const TransportationsTable: React.FC<Props> = ({ readOnly = false }) => {
  const { data: rawEntities } = useIncidentMedicalTransportations('incident');
  const entities = (rawEntities ?? []).filter((e) => !e.archived);

  const incidentId = useActualIncidentId();
  const recordVersion = useRecordMedicalPlanVersion();
  const { mutateAsync: createRecord, isPending: isCreating } =
    useCreateIncidentMedicalTransportation();
  const { mutateAsync: editRecord, isPending: isEditing } =
    useEditIncidentMedicalTransportation();

  const onCreate = useCallback(
    async (draft: MedicalPlanDraft) => {
      await createRecord({
        incident_id: incidentId,
        ambulance_service: draft.ambulance_service as string,
        address: draft.address as string,
        contact_number: draft.contact_number as string,
        paramedics_on_board: draft.paramedics_on_board as boolean,
        als: draft.als as boolean,
        bls: draft.bls as boolean,
      });
      recordVersion();
    },
    [createRecord, incidentId, recordVersion]
  );

  const onUpdate = useCallback(
    async (entity: IncidentMedicalTransportation, draft: MedicalPlanDraft) => {
      await editRecord(toEditPayload(entity, draft));
      recordVersion();
    },
    [editRecord, recordVersion]
  );

  const onDelete = useCallback(
    async (entity: IncidentMedicalTransportation) => {
      await editRecord(toEditPayload(entity, { archived: true }));
      recordVersion();
    },
    [editRecord, recordVersion]
  );

  return (
    <MedicalPlanTable
      testId="ics206-transportation"
      entityNoun="Ambulance service"
      entityNounPlural="Ambulance services"
      addButtonLabel="Add Transport"
      noDataMessage="No transportation providers defined."
      entities={entities}
      columns={COLUMNS}
      emptyDraft={EMPTY_DRAFT}
      requiredField="ambulance_service"
      rowLabelField="ambulance_service"
      isSaving={isCreating || isEditing}
      onCreate={onCreate}
      onUpdate={onUpdate}
      onDelete={onDelete}
      readOnly={readOnly}
    />
  );
};
